import axios from 'axios';
import type { TTSVoice, TTSRequest } from '../types';
import { getVoiceName, generatePreviewMessage } from '../utils/voiceUtils';
import { serializeError } from '../utils/apiUtils';

const API_ENDPOINT = 'https://texttospeech.googleapis.com/v1';
const API_KEY = import.meta.env.VITE_GOOGLE_CLOUD_API_KEY;

// Configuration
const TIMEOUT_MS = 30000; // 30 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const retryWithBackoff = async <T>(
  operation: () => Promise<T>,
  retries: number = MAX_RETRIES,
  delay: number = RETRY_DELAY
): Promise<T> => {
  try {
    return await operation();
  } catch (error: any) {
    if (retries > 0 && (
      error.code === 'ECONNABORTED' ||
      error.response?.status === 503 ||
      error.code === 'ERR_NETWORK'
    )) {
      await new Promise(resolve => setTimeout(resolve, delay));
      console.log(`Retrying Google TTS operation... (${retries} attempts left)`);
      return retryWithBackoff(operation, retries - 1, delay * 2);
    }
    throw error;
  }
};

export const fetchGoogleVoices = async (): Promise<TTSVoice[]> => {
  try {
    if (!API_KEY) {
      throw new Error('Google Cloud API key not configured');
    }

    const fetchVoices = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

      try {
        const response = await axios.get(`${API_ENDPOINT}/voices`, {
          params: { key: API_KEY },
          signal: controller.signal,
          timeout: TIMEOUT_MS
        });

        clearTimeout(timeout);

        if (!response.data?.voices || !Array.isArray(response.data.voices)) {
          throw new Error('Invalid response format from Google Cloud API');
        }

        return response.data.voices;
      } catch (error) {
        clearTimeout(timeout);
        throw error;
      }
    };

    const voices = await retryWithBackoff(fetchVoices);

    // Log all raw voices from Google API to understand the structure
    console.log('Raw Google API voices (first 10):', voices.slice(0, 10).map((v: any) => ({ 
      name: v.name, 
      languageCodes: v.languageCodes,
      ssmlGender: v.ssmlGender,
      naturalSampleRateHertz: v.naturalSampleRateHertz
    })));
    
    // Log specifically the star-named voices to see their full structure
    const starVoice = voices.find((v: any) => v.name === 'Achernar');
    if (starVoice) {
      console.log('🌟 Full structure of "Achernar" voice:', starVoice);
    }

    const mappedVoices = voices
      .filter((voice: any) => {
        // Filter out incomplete star-named voices that don't have language code prefix
        // These voices like "Achernar", "Achird" etc. require additional model parameters
        // and are not directly usable. Keep only properly formatted voice IDs like:
        // - "en-US-Standard-A"
        // - "en-US-Neural2-A"
        // - "en-US-Chirp3-HD-Achernar" (full path with star name)
        const hasLanguagePrefix = voice.name.includes('-');
        if (!hasLanguagePrefix) {
          console.log(`⏭️ Skipping incomplete voice: ${voice.name} (missing language prefix)`);
        }
        return hasLanguagePrefix;
      })
      .map((voice: any) => {
        const languageCode = voice.languageCodes[0];
        const gender = voice.ssmlGender.toLowerCase();
        const voiceType = voice.name.includes('Neural2') ? 'Neural2' : 
                         voice.name.includes('Studio') ? 'Studio' :
                         voice.name.includes('Wavenet') ? 'Wavenet' :
                         voice.name.includes('Chirp') ? 'Premium' : 'Standard';
        
        const displayName = getVoiceName(voice.name, gender);
        const previewText = generatePreviewMessage(displayName, languageCode);

        return {
          id: voice.name, // MUST be the real Google voice ID (e.g., "en-US-Standard-A")
          name: displayName, // Display name (e.g., "Margaret")
          gender,
          language: languageCode,
          accent: languageCode,
          type: voiceType,
          quality: voiceType === 'Standard' ? 'standard' : 'premium',
          provider: 'google' as const,
          style: 'general',
          previewText,
          tags: [
            voiceType.toLowerCase(),
            gender,
            languageCode,
            'tts'
          ],
          naturalSampleRateHertz: voice.naturalSampleRateHertz,
          supportsSpeed: voiceType !== 'Standard',
          supportsPitch: voiceType !== 'Standard'
        };
      });

    console.log(`✅ Fetched ${mappedVoices.length} Google voices (filtered from ${voices.length} total)`);
    return mappedVoices;
  } catch (error: any) {
    console.error('Failed to fetch Google voices:', serializeError(error));
    throw error;
  }
};

export const synthesizeGoogleSpeech = async (request: TTSRequest): Promise<string> => {
  try {
    if (!API_KEY) {
      throw new Error('Google Cloud API key not configured');
    }

    // Note: Google now uses both traditional IDs (en-US-Standard-A) and star names (Achernar, Achird, etc.)
    // Both are valid Google voice IDs

    const synthesize = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

      try {
        const audioConfig: any = {
          audioEncoding: 'MP3',
          volumeGainDb: request.settings.volume
        };

        if (request.voice.supportsPitch) {
          audioConfig.pitch = request.settings.pitch;
        }
        if (request.voice.supportsSpeed) {
          audioConfig.speakingRate = request.settings.speed;
        }

        const response = await axios.post(
          `${API_ENDPOINT}/text:synthesize`,
          {
            input: {
              text: request.text
            },
            voice: {
              languageCode: request.voice.language,
              name: request.voice.id
            },
            audioConfig
          },
          {
            params: { key: API_KEY },
            signal: controller.signal,
            timeout: TIMEOUT_MS
          }
        );

        clearTimeout(timeout);

        if (!response.data?.audioContent) {
          throw new Error('No audio content received from API');
        }

        return `data:audio/mp3;base64,${response.data.audioContent}`;
      } catch (error) {
        clearTimeout(timeout);
        throw error;
      }
    };

    return await retryWithBackoff(synthesize);
  } catch (error: any) {
    console.error('Google speech synthesis failed:', serializeError(error));
    throw error;
  }
};